import { useState, useEffect, useContext } from "react";
import "./editProfileModal.css";
import API from "../utils/API";
import { AuthContext } from "../utils/AuthContext";

export default function EditDivisionModal({ division, onSave }) {
    const { isAdmin } = useContext(AuthContext);

    const [divisionNameEntry, setDivisionNameEntry] = useState("");
    const [streetAddressEntry, setStreetAddressEntry] = useState("");
    const [suiteNumberEntry, setSuiteNumberEntry] = useState("");
    const [cityEntry, setCityEntry] = useState("");
    const [stateEntry, setStateEntry] = useState("");
    const [zipCodeEntry, setZipCodeEntry] = useState("");
    const [websiteEntry, setWebsiteEntry] = useState("");
    const [phoneEntry, setPhoneEntry] = useState("");
    const [divisionTypeEntry, setDivisionTypeEntry] = useState("");
    const [districtEntry, setDistrictEntry] = useState("");
    const [activeDivision, setActiveDivision] = useState(true);

    const [isFormValid, setIsFormValid] = useState(false);

    useEffect(() => {
        if (!division) return;
        setDivisionNameEntry(division.division_name || "");
        setStreetAddressEntry(division.street_address || "");
        setSuiteNumberEntry(division.suite_number || "");
        setCityEntry(division.city || "");
        setStateEntry(division.state || "");
        setZipCodeEntry(division.zip_code || "");
        setWebsiteEntry(division.website || "");
        setPhoneEntry(formatPhoneNumber(division.division_phone) || "");
        setDivisionTypeEntry(division.division_type || "");
        setDistrictEntry(division.district || "");
        setActiveDivision(division.active_division ?? true);
    }, [division]);

    useEffect(() => {
        setIsFormValid(!!divisionNameEntry);
    }, [divisionNameEntry]);

    const formatPhoneNumber = (value) => {
        if (!value) return "";
        const cleaned = ('' + value).replace(/\D/g, '');
        const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
        return match ? `(${match[1]}) ${match[2]}-${match[3]}` : value;
    };

    const toProperCase = (str) => {
        const stopWords = ["of", "and", "the"];
        return str.toLowerCase().split(" ").map((word, i) =>
            i === 0 || !stopWords.includes(word)
                ? word.charAt(0).toUpperCase() + word.slice(1)
                : word
        ).join(" ");
    };

    const handleSave = async () => {
        try {
            const data = {
                division_id: division.division_id,
                division_name: divisionNameEntry,
                street_address: streetAddressEntry,
                suite_number: suiteNumberEntry,
                city: cityEntry,
                state: stateEntry,
                zip_code: zipCodeEntry,
                website: websiteEntry,
                division_phone: phoneEntry,
                division_type: divisionTypeEntry,
                district: districtEntry.trim().toUpperCase() || null,
                ...(isAdmin && { active_division: activeDivision }),
            };
            const response = await API.updateDivision(data);
            onSave(response.data);
            document.getElementById("editDivisionModal")
                ?.querySelector('[data-bs-dismiss="modal"]')?.click();
        } catch (err) {
            console.error("Error updating division:", err);
        }
    };

    return (
        <>
        <div className="modal fade edit-bio-modal" id="editDivisionModal" tabIndex="-1" aria-hidden="true">
            <div className="modal-dialog">
                <div className="modal-content edit-profile-modal-content position-absolute top-50 start-50 translate-middle-x">
                    <div className="modal-header edit-bio-modal-header">
                        <h5 className="modal-title modal-title display-6">Edit Division Information</h5>
                    </div>
                    <div className="modal-body px-4">
                        <div className="edit-profile-body">

                            <div className="row mt-2">
                                <div className="input-group mb-2 input-group-sm">
                                    <input
                                        className="form-control"
                                        placeholder="Division Name*"
                                        value={divisionNameEntry}
                                        onChange={(e) => setDivisionNameEntry(e.target.value)}
                                    />
                                </div>
                            </div>

                            <div className="row py-1">
                                <div className="input-group input-group-sm">
                                    <input
                                        className="form-control"
                                        placeholder="Division Type (e.g. Office, Bureau)"
                                        value={divisionTypeEntry}
                                        onChange={(e) => setDivisionTypeEntry(e.target.value)}
                                    />
                                </div>
                            </div>

                            {divisionTypeEntry === "Member" && (
                                <div className="row py-1">
                                    <div className="input-group input-group-sm">
                                        <input
                                            className="form-control"
                                            placeholder="Legislative District (e.g. AD62, SD27)"
                                            value={districtEntry}
                                            onChange={(e) => setDistrictEntry(e.target.value)}
                                        />
                                    </div>
                                </div>
                            )}

                            <div className="input-group py-1">
                                <div className="row address-row">
                                    <div className="col-9 px-0 me-0 input-group-sm">
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Address"
                                            value={streetAddressEntry}
                                            onChange={(e) => setStreetAddressEntry(toProperCase(e.target.value))}
                                        />
                                    </div>
                                    <div className="col-3 pe-0 me-0 input-group-sm">
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Suite #"
                                            value={suiteNumberEntry}
                                            onChange={(e) => setSuiteNumberEntry(e.target.value)}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="input-group py-1">
                                <div className="row">
                                    <div className="col-6 pe-0 me-0 input-group-sm">
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="City"
                                            value={cityEntry}
                                            onChange={(e) => setCityEntry(toProperCase(e.target.value))}
                                        />
                                    </div>
                                    <div className="col-3 pe-0 me-0 input-group-sm">
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="State"
                                            value={stateEntry}
                                            onChange={(e) => setStateEntry(e.target.value.toUpperCase())}
                                        />
                                    </div>
                                    <div className="col-3 input-group-sm">
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="ZIP"
                                            value={zipCodeEntry}
                                            onChange={(e) => setZipCodeEntry(e.target.value)}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="row py-1">
                                <div className="input-group input-group-sm">
                                    <span className="input-group-text phone-span"><i className="bi bi-globe" />&nbsp;Website</span>
                                    <input
                                        className="form-control"
                                        placeholder="Division URL"
                                        value={websiteEntry}
                                        onChange={(e) => setWebsiteEntry(e.target.value)}
                                    />
                                </div>
                            </div>

                            <div className="row py-1">
                                <div className="col-md-6">
                                    <div className="input-group input-group-sm mb-2">
                                        <span className="input-group-text phone-span"><i className="bi bi-telephone" />&nbsp;Phone</span>
                                        <input
                                            className="form-control"
                                            placeholder="916-555-0123"
                                            value={phoneEntry}
                                            onChange={(e) => setPhoneEntry(formatPhoneNumber(e.target.value))}
                                        />
                                    </div>
                                </div>
                            </div>

                            {isAdmin && (
                                <div className="row py-2 mt-1 border-top">
                                    <div className="col-12 d-flex align-items-center gap-3">
                                        <div className="form-check form-switch mb-0">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                role="switch"
                                                id="activeDivisionToggle"
                                                checked={activeDivision}
                                                onChange={(e) => setActiveDivision(e.target.checked)}
                                            />
                                            <label className="form-check-label" htmlFor="activeDivisionToggle">
                                                {activeDivision ? "Active" : "Inactive"}
                                            </label>
                                        </div>
                                        <small className="text-muted">
                                            {activeDivision
                                                ? "Division is visible and included in directory listings."
                                                : "Division is hidden but its records are preserved."}
                                        </small>
                                    </div>
                                </div>
                            )}

                        </div>
                    </div>
                    <div className="modal-footer pt-0">
                        <button type="button" className="btn modal-close" data-bs-dismiss="modal">Cancel</button>
                        <div className={isFormValid ? "" : "disabled-button"}>
                            <button
                                type="button"
                                className="btn modal-save"
                                data-bs-dismiss="modal"
                                disabled={!isFormValid}
                                onClick={handleSave}
                            >
                                Save changes
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        </>
    );
}
